

import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Snackbar,
  Alert,
  Card,
  CardContent,
  Grid,
} from '@mui/material';
import { jwtDecode } from 'jwt-decode';
import axios from 'axios';
import { API_BASE_URL } from '../../constants/constants';
import { usePermissions } from '../../context/PermissionContext';
import Loader from '../General/Loader'; // Adjust the import path as needed
import BackButton from '../../components/BackButton';

const SetAdPricing = () => {
  const [bannerPricing, setBannerPricing] = useState(null);
  const [fullpagePricing, setFullpagePricing] = useState(null);
  const [newBannerPricing, setNewBannerPricing] = useState('');
  const [newFullpagePricing, setNewFullpagePricing] = useState('');
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [currentAdType, setCurrentAdType] = useState(null);
  const [snackOpen, setSnackOpen] = useState(false);
  const [snackMessage, setSnackMessage] = useState('');
  const [snackSeverity, setSnackSeverity] = useState('success');
  const { can } = usePermissions();
  const canEdit = can('master.adPrice.edit');

  useEffect(() => {
    const token = sessionStorage.getItem('token');
    if (token) {
      try {
        jwtDecode(token);
        fetchAdPricing();
      } catch (error) {
        console.error('Error decoding token:', error);
        setSnackSeverity('error');
        setSnackMessage('Authentication error. Please log in again.');
        setSnackOpen(true);
        setLoading(false);
      }
    } else {
      setSnackSeverity('error');
      setSnackMessage('Authentication required. Please log in.');
      setSnackOpen(true);
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchAdPricing = async () => {
    try {
      setLoading(true);
      const token = sessionStorage.getItem('token');
      
      // Fetch banner pricing
      const bannerResponse = await axios.get(`${API_BASE_URL}/adPricing/banner`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      setBannerPricing(bannerResponse.data.pricing);
      
      // Fetch fullpage pricing
      const fullpageResponse = await axios.get(`${API_BASE_URL}/adPricing/fullpage`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      setFullpagePricing(fullpageResponse.data.pricing);
    } catch (error) {
      console.error('Error fetching ad pricing:', error);
      setSnackSeverity('error');
      setSnackMessage('Failed to fetch current ad pricing.');
      setSnackOpen(true);
    } finally {
      setLoading(false);
    }
  };

  const handleBannerPricingChange = (e) => {
    setNewBannerPricing(e.target.value);
  };

  const handleFullpagePricingChange = (e) => {
    setNewFullpagePricing(e.target.value);
  };

  const handleUpdateClick = (adType) => {
    if (!canEdit) return;
    setCurrentAdType(adType);
    setDialogOpen(true);
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
    setCurrentAdType(null);
  };

  const handleConfirmUpdate = async () => {
    if (!canEdit) return;
    setDialogOpen(false);
    setLoading(true);
    
    try {
      const token = sessionStorage.getItem('token');
      const pricing = currentAdType === 'banner' 
        ? parseFloat(newBannerPricing) 
        : parseFloat(newFullpagePricing);
      
      await axios.post(
        `${API_BASE_URL}/adPricing`,
        { adType: currentAdType, pricing },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      setSnackSeverity('success');
      setSnackMessage(`${currentAdType.charAt(0).toUpperCase() + currentAdType.slice(1)} ad pricing updated successfully.`);
      setSnackOpen(true);
      
      // Reset the respective input field
      if (currentAdType === 'banner') {
        setNewBannerPricing('');
      } else {
        setNewFullpagePricing('');
      }
      
      // Refresh data
      fetchAdPricing();
    } catch (error) {
      console.error('Error updating ad pricing:', error);
      setSnackSeverity('error');
      setSnackMessage(error.response?.data?.message || 'Failed to update ad pricing.');
      setSnackOpen(true);
      setLoading(false);
    }
  };

  const handleSnackClose = () => {
    setSnackOpen(false);
  };

  if (loading) {
    return <Loader />;
  }

  return (
    <Box sx={{ position: 'relative', padding: 2 }}>
    {/* Place BackButton outside the Grid */}
    <BackButton />
    <br/>  <br/>
    <Box sx={{ maxWidth: 800, mx: 'auto', mt: 5 }}>
      <Typography variant="h4" gutterBottom sx={{ fontFamily: 'Merriweather, serif', textAlign: 'center', mb: 4 }}>
        Advertisement Pricing
      </Typography>
      
      <Grid container spacing={4}>
        {/* Banner Ad Pricing Card */}
        <Grid item xs={12} md={6}>
          <Card sx={{ p: 3, boxShadow: 3, height: '100%' }}>
            <CardContent>
              <Typography variant="h5" gutterBottom sx={{ fontFamily: 'Merriweather, serif' }}>
                Banner Ad
              </Typography>
              {bannerPricing !== null ? (
                <>
                  <Typography variant="h6" gutterBottom>
                    Current Price: ₹{bannerPricing}
                  </Typography>
                  {canEdit && (
                    <>
                      <TextField
                        label="New Price"
                        type="number"
                        fullWidth
                        margin="normal"
                        variant="outlined"
                        value={newBannerPricing}
                        onChange={handleBannerPricingChange}
                        InputProps={{ inputProps: { min: 0 } }}
                      />
                      <Button
                        variant="contained"
                        color="primary"
                        onClick={() => handleUpdateClick('banner')}
                        disabled={newBannerPricing === '' || newBannerPricing < 0}
                        sx={{ mt: 2 }}
                        fullWidth
                      >
                        Update Banner Price
                      </Button>
                    </>
                  )}
                </>
              ) : (
                <Typography variant="body1" color="textSecondary">
                  No banner pricing set yet.
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>
        
        {/* Fullpage Ad Pricing Card */}
        <Grid item xs={12} md={6}>
          <Card sx={{ p: 3, boxShadow: 3, height: '100%' }}>
            <CardContent>
              <Typography variant="h5" gutterBottom sx={{ fontFamily: 'Merriweather, serif' }}>
                Fullpage Ad
              </Typography>
              {fullpagePricing !== null ? (
                <>
                  <Typography variant="h6" gutterBottom>
                    Current Price: ₹{fullpagePricing}
                  </Typography>
                  {canEdit && (
                    <>
                      <TextField
                        label="New Price"
                        type="number"
                        fullWidth
                        margin="normal"
                        variant="outlined"
                        value={newFullpagePricing}
                        onChange={handleFullpagePricingChange}
                        InputProps={{ inputProps: { min: 0 } }}
                      />
                      <Button
                        variant="contained"
                        color="primary"
                        onClick={() => handleUpdateClick('fullpage')}
                        disabled={newFullpagePricing === '' || newFullpagePricing < 0}
                        sx={{ mt: 2 }}
                        fullWidth
                      >
                        Update Fullpage Price
                      </Button>
                    </>
                  )}
                </>
              ) : (
                <Typography variant="body1" color="textSecondary">
                  No fullpage pricing set yet.
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Confirmation Dialog */}
      <Dialog open={dialogOpen} onClose={handleDialogClose}>
        <DialogTitle>Confirm Update</DialogTitle>
        <DialogContent>
          <Typography>
            {currentAdType === 'banner' 
              ? `Are you sure you want to update the Banner Ad price to ₹${newBannerPricing}?`
              : `Are you sure you want to update the Fullpage Ad price to ₹${newFullpagePricing}?`
            }
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDialogClose} color="secondary">
            Cancel
          </Button>
          <Button onClick={handleConfirmUpdate} color="primary">
            Confirm
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar Notification */}
      <Snackbar
        open={snackOpen}
        autoHideDuration={6000}
        onClose={handleSnackClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={handleSnackClose} severity={snackSeverity} sx={{ width: '100%' }}>
          {snackMessage}
        </Alert>
      </Snackbar>
    </Box>
    </Box>
  );
};

export default SetAdPricing;